import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { wipInventory, type WipInventoryListItem } from '@/api/wipInventory';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';

const PERMISSION = 'production.wip_inventory.manage';

const STATUS_LABEL: Record<string, string> = {
  draft: 'чернетка',
  posted: 'проведено',
  cancelled: 'скасовано',
};

export default function WipInventoryPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29 — client-side limit 500 не тримає
  // обсяги після клону 1С).
  const { datasource, total } = useInfiniteGridDatasource<WipInventoryListItem>(
    (limit, offset, sort) => wipInventory.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
  );

  const cols = useMemo<ColDef<WipInventoryListItem>[]>(() => [
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата документа', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'inventory_date', headerName: 'Дата інвентаризації', width: 180, sortable: false,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    { field: 'warehouse_id', headerName: 'Склад', sortable: false },
    {
      field: 'status', headerName: 'Статус', width: 150, sortable: true,
      valueFormatter: (p) => (p.value ? STATUS_LABEL[p.value as string] ?? String(p.value) : ''),
    },
  ], [orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Інвентаризацію НЗВ у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Інвентаризація НЗВ<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/wip-inventory/new')}
        >
          + Нова інвентаризація
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<WipInventoryListItem>
        deleteRow={{ remove: (id) => wipInventory.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="wip-inventory"
        onRowDoubleClicked={(r) => navigate(`/wip-inventory/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
